/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import { OperationHandler } from 'src/core/services/api/createOperationCall';
import { fileService } from 'src/core/services/api/fileService';
import { getAzureBlobService } from 'src/core/services/azure/azureBlobService';
import { getAzureService } from 'src/core/services/azure/azureService';
import { AzureBlobSwagger } from 'src/core/services/azure/azureSwaggers';
import { getCustomSkillGlobalsService } from 'src/core/services/customSkillGlobalService';
import { AppStore } from 'src/core/stores/app/AppStore';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { CustomSkillGlobalsStore } from 'src/core/stores/CustomSkillGlobalsStore';
import { EntityStore } from 'src/core/types/EntityStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { batchPromises, getNextAbleResults } from 'src/core/utils/batchPromises';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getCustomTextProjectService } from 'src/customText/core/services/projects/customTextProjectService';
import {
    CustomTextExportedProjectSwaggerOldVersion,
    CustomTextModelTypeSwaggerOldVersion
} from 'src/customText/core/services/projects/customTextProjectSwaggerOldVersion';
import {
    CustomTextCopyProjectSwagger,
    CustomTextExportedProjectSwagger,
    CustomTextProjectJobStatusSwagger
} from 'src/customText/core/services/projects/customTextProjectSwaggers';
import { CustomTextClassificationStore } from 'src/customText/core/stores/CustomTextClassificationStore';
import { CustomTextClassStore } from 'src/customText/core/stores/CustomTextClassStore';
import { CustomTextDocumentStore, emptyCustomTextDocumentMetadata } from 'src/customText/core/stores/CustomTextDocumentStore';
import { CustomTextExtractionStore } from 'src/customText/core/stores/CustomTextExtractionStore';
import { CustomTextProjectKind, CustomTextProjectStore } from 'src/customText/core/stores/CustomTextProjectStore';
import { LabelsFileNamesStore } from 'src/customText/core/stores/LabelsFileNamesStore';
import {
    documentMetadataToDocumentStore,
    exportedProjectSwaggerToDocumentAndTagStores,
    projectStoreToExportedProjectSwagger,
    projectSwaggerToProjectStore
} from 'src/customText/modules/handlers/projects/projectMappers';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { TrainingConfigVersionSwagger } from 'src/modules/modelTraining/types/trainingSwaggers';
import { TrainingConfigVersion } from 'src/modules/modelTraining/types/trainingTypes';

export type CustomTextProjectHandlerGroup = ReturnType<typeof createCustomTextProjectHandlerGroup>;

export const loadCustomTextProjects = async (serviceKey?: ServiceKey): Promise<CustomTextProjectStore[]> => {
    const projectSwaggers = await getNextAbleResults(getCustomTextProjectService().getProjects, 'value', [serviceKey]);

    return projectSwaggers.map(s => projectSwaggerToProjectStore(s, []));
};

/**
 * Represents a handler group for custom text project operations.
 *
 * @param dependencies The handler group dependencies.
 */
export const createCustomTextProjectHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore, lifetime } = dependencies;
    const azureBlobService = getAzureBlobService();

    const documentsInCurrentProjectStore = new Map<string, boolean>();

    let nextBatchMarker = '';

    const getExportPollOperationHandler = (): OperationHandler<CustomTextProjectJobStatusSwagger> => ({
        retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
        timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
        handleCall: async data => {
            if (!lifetime.isDisposed && data.status === 'failed') {
                throw new Error(data.errors[0]?.message);
            }

            if (lifetime.isDisposed || data.status !== 'succeeded') {
                return;
            }

            const results = await getCustomTextProjectService().getExportProjectResults({
                resultUrl: data.resultUrl
            });
            try {
                mapJsonExportedResultsToStore(results);
            } catch (e) {
                throw new Error(
                    'Error loading your project. Please make sure that your tags file in the container was not manually edited and try importing your project again.'
                );
            }
        }
    });

    const mapJsonExportedResultsToStore = (results: CustomTextExportedProjectSwagger) => {
        const { currentCustomTextProjectStore } = rootStore;

        const { documents, tagStores } = exportedProjectSwaggerToDocumentAndTagStores(
            results,
            currentCustomTextProjectStore.documents,
            currentCustomTextProjectStore.projectKind
        );

        if (results.metadata.projectKind === 'CustomEntityRecognition') {
            currentCustomTextProjectStore.setTagsStore(
                new CustomTextExtractionStore(<EntityStore[]>tagStores),
                results.metadata.projectKind
            );
        } else {
            currentCustomTextProjectStore.setTagsStore(
                new CustomTextClassificationStore(<CustomTextClassStore[]>tagStores),
                results.metadata.projectKind
            );
        }

        currentCustomTextProjectStore.setProjectDocuments(documents);
        if (currentCustomTextProjectStore.currentDocumentStore) {
            const updatedCurrentDocumentStore = documents.find(d => d.name === currentCustomTextProjectStore.currentDocumentStore.name);
            if (updatedCurrentDocumentStore) {
                currentCustomTextProjectStore.setCurrentDocumentStore(documentMetadataToDocumentStore(updatedCurrentDocumentStore));
            }
        }
    };

    const resetStoreToExportedData = async ({}: HandlerParams<object>) => {
        const { currentCustomTextProjectStore } = rootStore;

        mapJsonExportedResultsToStore(currentCustomTextProjectStore.jsonExportedData);
        currentCustomTextProjectStore.setIsDirty(false);
    };

    const getImportPollOperationHandler = (): OperationHandler<CustomTextProjectJobStatusSwagger> => ({
        timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
        retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
        handleCall: data => {
            if (data.status === 'failed') {
                const error = data.errors?.[0];

                throw new Error(`${error.message} ${error.innererror ? `: ${error.innererror.message}` : ''}`);
            }
        }
    });

    const getCustomTextProjectTrainingConfigVersions = async (projectKind: CustomTextProjectKind) => {
        const { customTextGlobalsStoreHouse } = rootStore;

        let globalsStore = customTextGlobalsStoreHouse.items.find(i => i.id === projectKind);
        if (!globalsStore) {
            globalsStore = new CustomSkillGlobalsStore(projectKind);
            customTextGlobalsStoreHouse.add(globalsStore);
        }

        if (!globalsStore.trainingConfigVersions?.length) {
            const results = await getNextAbleResults<'value', TrainingConfigVersionSwagger>(
                (nextLink: string) => getCustomSkillGlobalsService().getTrainingConfigVersions(projectKind, nextLink),
                'value'
            );

            globalsStore.setTrainingConfigVersions(
                results.map(
                    r =>
                        <TrainingConfigVersion>{
                            modelExpirationDate: r?.modelExpirationDate ? new Date(r.modelExpirationDate) : undefined,
                            trainingConfigVersion: r.trainingConfigVersion,
                            isLatest: r.isLatest
                        }
                )
            );
        }
    };

    const getCustomTextProjects = async (_?: HandlerParams) => {
        const { customTextProjectStoreHouse } = rootStore;

        try {
            customTextProjectStoreHouse.tracker.setState('running');

            const [projectStores] = await Promise.all([loadCustomTextProjects(), getCustomTextCultures({})]);

            transaction(() => {
                customTextProjectStoreHouse.setItems(
                    projectStores.filter(
                        p =>
                            p.projectKind === 'CustomMultiLabelClassification' ||
                            p.projectKind === 'CustomSingleLabelClassification' ||
                            p.projectKind === 'CustomEntityRecognition'
                    )
                );
                customTextProjectStoreHouse.tracker.setState('completed');
            });
        } catch (err) {
            customTextProjectStoreHouse.setItems([]);
            customTextProjectStoreHouse.tracker.setState('failed', <Error>err);

            throw err;
        }
    };

    const getCustomTextCultures = async ({}: HandlerParams<object>) => {
        const { customTextCultureStorehouse } = rootStore;

        if (customTextCultureStorehouse.tracker.isRunning || customTextCultureStorehouse.tracker.isCompleted) {
            return;
        }

        try {
            customTextCultureStorehouse.tracker.setState('running');
            const culturesSwaggers = await getNextAbleResults(getCustomSkillGlobalsService().getProjectCultures, 'value');
            const cultureStores = culturesSwaggers.map(swagger => ({
                name: swagger.languageName,
                id: swagger.languageCode,
                code: swagger.languageCode,
                isoCode: swagger.languageCode
            }));
            customTextCultureStorehouse.setItems(cultureStores);
            customTextCultureStorehouse.tracker.setState('completed');
        } catch (e) {
            customTextCultureStorehouse.tracker.setState('failed', <Error>e);

            throw e;
        }
    };

    const fetchCustomTextProjectAvailableLabelsFileNames = async ({
        localStore,
        containerName,
        storageAccountName,
        prefix
    }: HandlerParams<{
        containerName: string;
        storageAccountName: string;
        localStore: LabelsFileNamesStore;
        prefix?: string;
    }>) => {
        const { storageToken } = rootStore.azureStore.session.userTokens;
        localStore.tracker.setState('running');
        try {
            const allBlobs = await azureBlobService.getAzureBlobsByContainerName({
                storageToken,
                containerName,
                storageAccountName,
                prefix
            });

            const labelsFilesNames = allBlobs.filter(documentSwagger => documentSwagger.name.includes('.json')).map(f => ({ id: f.name }));

            const canDisplayLabelFiles = allBlobs.length < LuisConstants.CustomText.MaximumLabelsFilesDisplayed || Boolean(prefix);
            localStore.setCanDisplayLabelFiles(canDisplayLabelFiles);

            localStore.setItems(canDisplayLabelFiles ? labelsFilesNames : []);
            localStore.tracker.setState('completed');
        } catch (err) {
            localStore.tracker.setState('failed', <Error>err);
        }
    };

    const updateCustomTextProject = async ({
        name,
        multiLingual,
        description,
        oosThreshold
    }: HandlerParams<Pick<CustomTextProjectStore, 'name' | 'description' | 'multiLingual' | 'oosThreshold'>>) => {
        const projectToUpdate = rootStore.customTextProjectStoreHouse.findById(name);

        await getCustomTextProjectService().createOrUpdateProject({
            projectName: name,
            description,
            multiLingual,
            language: projectToUpdate.culture,
            projectKind: projectToUpdate.projectKind,
            storageInputContainerName: projectToUpdate.containerName,
            settings: { confidenceThreshold: oosThreshold }
        });

        projectToUpdate.setMultiLingual(multiLingual);
        projectToUpdate.setDescription(description);
        projectToUpdate.setOosThreshold(oosThreshold);
    };

    const deleteCustomTextProject = async ({
        projectNames,
        appsLocalStoreHouse,
        generateMultiFailureMessage
    }: HandlerParams<{
        projectNames: string[];
        appsLocalStoreHouse?: ResourceStoreHouse<AppStore>;
        generateMultiFailureMessage: (failedCount: number) => string;
    }>) => {
        const { customTextProjectStoreHouse: customTextProjectsStoreHouse } = rootStore;
        const storeHouse = appsLocalStoreHouse || customTextProjectsStoreHouse;
        storeHouse.tracker.setState('running');

        const { results, failedCount } = await batchPromises(
            projectNames.map(projectName =>
                getCustomTextProjectService().deleteProject({
                    projectName,
                    operationHandle: {
                        timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                        retryStrategy: res => res.data.status !== 'succeeded' && res.data.status !== 'failed',
                        handleCall: data => {
                            if (data.status === 'failed') {
                                throw new Error(data.errors?.[0]?.message);
                            }
                        }
                    }
                })
            )
        );

        const succeededIds = results.filter(res => res.status === 'success').map((_, index) => projectNames[index]);

        if (failedCount > 0) {
            throw new Error(generateMultiFailureMessage(failedCount));
        }

        transaction(() => {
            storeHouse.batchRemoveByIds(succeededIds);
            storeHouse.tracker.setState('completed');
        });
    };

    const fetchCustomTextProjectDocuments = async ({
        containerName,
        storageAccountName,
        storageToken,
        prefix
    }: HandlerParams<{
        containerName: string;
        storageAccountName: string;
        storageToken: string;
        prefix?: string;
    }>): Promise<AzureBlobSwagger[]> => {
        const allBlobs = await azureBlobService.getAzureBlobsByContainerName({
            containerName,
            storageAccountName,
            storageToken,
            prefix,
            allPortions: true
        });

        return allBlobs.filter(documentSwagger => documentSwagger.name.includes('.txt'));
    };

    const fetchCustomTextProjectDocumentsInBatches = async ({
        containerName,
        storageAccountName,
        storageToken,
        containerMarker
    }: HandlerParams<{ containerName: string; storageAccountName: string; storageToken: string; containerMarker?: string }>): Promise<{
        blobs: AzureBlobSwagger[];
        nextMarker: string;
    }> => {
        try {
            const { blobs, nextMarker } = await azureBlobService.getAzureBlobsByContainerNameInBatches({
                containerName,
                storageAccountName,
                storageToken,
                marker: containerMarker
            });
            rootStore.setAccessError(false, undefined);
            return { blobs: blobs.filter(documentSwagger => documentSwagger.name.includes('txt')), nextMarker };
        } catch (err) {
            rootStore.setAccessError(true, err.response?.status);
        }
    };
    const createCustomTextProject = async ({
        projectName,
        projectKind,
        projectDescription,
        defaultLanguage,
        containerName,
        multiLingual
    }: HandlerParams<{
        projectName: string;
        projectDescription: string;
        defaultLanguage: string;
        containerName: string;
        multiLingual: boolean;
        projectKind: CustomTextProjectKind;
    }>) => {
        await getCustomTextProjectService().createOrUpdateProject({
            projectName,
            projectKind,
            multiLingual,
            language: defaultLanguage,
            description: projectDescription,
            storageInputContainerName: containerName
        });
    };

    const copyCustomTextProject = async ({
        projectName,
        projectKind,
        allowOverwrite,
        targetResource,
        storageInputContainerName
    }: HandlerParams<{
        projectName: string;
        allowOverwrite: boolean;
        storageInputContainerName: string;
        projectKind: CustomTextProjectKind;
        targetResource: AzureCsAccountStore;
    }>) => {
        let keys = targetResource.keyStrings;

        if (!keys?.key1) {
            const tenantToken = rootStore.azureStore.session.userTokens.armToken;
            keys = await getAzureService().getAzureCsAccountKeyStringsByPath({ tenantToken, path: targetResource.path });
        }

        const authorizeCopyResponse: CustomTextCopyProjectSwagger = await getCustomTextProjectService().authorizeCopyProject({
            projectKind,
            projectName,
            allowOverwrite,
            serviceKey: keys.key1,
            storageInputContainerName,
            resourceId: targetResource.id,
            targetResourceEndpoint: targetResource.endpoint
        });

        await getCustomTextProjectService().copyProject({
            copyProjectSwagger: authorizeCopyResponse,
            operationHandle: {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                retryStrategy: res => res.data.status !== 'succeeded' && res.data.status !== 'failed',
                handleCall: data => {
                    if (data.status === 'failed') {
                        throw new Error(data.errors?.[0]?.message);
                    }
                }
            }
        });
    };

    const openCustomTextProjectWithBatchLoading = async ({ projectName }: HandlerParams<{ projectName: string }>) => {
        const [projectSwagger] = await Promise.all([getCustomTextProjectService().getProject({ projectName }), getCustomTextCultures({})]);

        await getCustomTextProjectTrainingConfigVersions(projectSwagger.projectKind);

        const { ownedStorageAccountInfo } = rootStore.azureStore.session.csAccountStore;
        const { storageToken } = rootStore.azureStore.session.userTokens;

        const { blobs: documentSwaggers, nextMarker } = await fetchCustomTextProjectDocumentsInBatches({
            containerName: projectSwagger.storageInputContainerName,
            storageAccountName: ownedStorageAccountInfo.name,
            storageToken
        });

        const documentsMetadata = documentSwaggers.map(d => ({
            ...emptyCustomTextDocumentMetadata,
            name: d.name,
            language: projectSwagger.language,
            contentSize: +d.size
        }));

        const exportStatusUrl = await getCustomTextProjectService().initiateExportProject({
            projectName,
            assetsToExport: ['all'],
            showAutoTag: projectSwagger.projectKind === 'CustomEntityRecognition'
        });
        const projectStore = projectSwaggerToProjectStore(projectSwagger, documentsMetadata);

        documentsMetadata.forEach(d => documentsInCurrentProjectStore.set(d.name, true));
        nextBatchMarker = nextMarker;

        transaction(() => {
            projectStore.setCsAccountPath(rootStore.azureStore.session.csAccountStore?.path);
            rootStore.customTextProjectStoreHouse.setItems([projectStore]);
            rootStore.setCurrentCustomTextProjectStore(projectStore);
            rootStore.currentCustomTextProjectStore.setNextBatchExists(Boolean(nextMarker));
        });

        await getCustomTextProjectService().pollExportProject({
            statusUrl: exportStatusUrl,
            operationHandler: getExportPollOperationHandler()
        });
    };

    const getExtractionExportProject = async ({ projectName }: HandlerParams<{ projectName: string }>) => {
        const exportStatusUrl = await getCustomTextProjectService().initiateExportProject({
            projectName,
            assetsToExport: ['all'],
            showAutoTag: true
        });

        await getCustomTextProjectService().pollExportProject({
            statusUrl: exportStatusUrl,
            operationHandler: getExportPollOperationHandler()
        });
    };

    const getCustomTextNextBatchOfDocuments = async (_?: HandlerParams) => {
        const { currentCustomTextProjectStore } = rootStore;
        const { ownedStorageAccountInfo } = rootStore.azureStore.session.csAccountStore;
        const { storageToken } = rootStore.azureStore.session.userTokens;

        const { blobs: nextBatch, nextMarker } = await fetchCustomTextProjectDocumentsInBatches({
            storageToken,
            containerMarker: nextBatchMarker,
            storageAccountName: ownedStorageAccountInfo.name,
            containerName: currentCustomTextProjectStore.containerName
        });

        const documentStores = nextBatch
            .map(d => new CustomTextDocumentStore({ name: d.name, language: currentCustomTextProjectStore.culture, contentSize: +d.size }))
            .filter(d => !documentsInCurrentProjectStore.has(d.name));

        documentStores.forEach(d => documentsInCurrentProjectStore.set(d.name, true));
        nextBatchMarker = nextMarker;

        transaction(() => {
            currentCustomTextProjectStore.setProjectDocuments(currentCustomTextProjectStore.documents.concat(documentStores));
            currentCustomTextProjectStore.setNextBatchExists(Boolean(nextMarker));
        });
    };

    const getCustomTextSpecificBatchWithPrefix = async ({ prefix }: HandlerParams<{ prefix: string }>) => {
        const { currentCustomTextProjectStore } = rootStore;
        const { ownedStorageAccountInfo } = rootStore.azureStore.session.csAccountStore;
        const { storageToken } = rootStore.azureStore.session.userTokens;

        const blobs = await fetchCustomTextProjectDocuments({
            prefix,
            storageToken,
            storageAccountName: ownedStorageAccountInfo.name,
            containerName: currentCustomTextProjectStore.containerName
        });

        const documentStores = blobs
            .map(d => new CustomTextDocumentStore({ name: d.name, language: currentCustomTextProjectStore.culture }))
            .filter(d => !documentsInCurrentProjectStore.has(d.name));

        documentStores.forEach(d => documentsInCurrentProjectStore.set(d.name, true));

        transaction(() => {
            currentCustomTextProjectStore.setProjectDocuments(currentCustomTextProjectStore.documents.concat(documentStores));
        });
    };

    const closeCustomTextProject = async () => {
        rootStore.setCurrentCustomTextProjectStore(null);

        // Cleaning the session storage from the cached docs for free some space up
        const sessionStorage = getStorage('sessionStorage');
        sessionStorage.clear();
    };

    const rebindStorageAccountToCurrentCsAccount = async ({
        storageAccountPath,
        tracker
    }: HandlerParams<{ storageAccountPath: string }>) => {
        const { csAccountStore } = rootStore.azureStore.session;
        const { armToken } = rootStore.azureStore.session.userTokens;

        try {
            await getAzureService().updateAzureCsAccountByPath({
                path: csAccountStore.path,
                tenantToken: armToken,
                identity: { type: 'SystemAssigned' },
                properties: { userOwnedStorage: [{ resourceId: storageAccountPath }] }
            });

            csAccountStore.setOwnedStorageId(storageAccountPath);
        } catch (err) {
            tracker.setState('failed', <Error>err);
            throw err;
        }
    };

    const updateCustomTextProjectAssets = async ({}: HandlerParams<object>) => {
        const { currentCustomTextProjectStore } = rootStore;

        const projectToImport = projectStoreToExportedProjectSwagger(currentCustomTextProjectStore);

        currentCustomTextProjectStore.setJsonExportedData(projectToImport);
        await getCustomTextProjectService().initiateAndPollImportProject({
            projectName: currentCustomTextProjectStore.name,
            projectToImport,
            operationHandler: getImportPollOperationHandler()
        });

        currentCustomTextProjectStore.setIsDirty(false);
    };

    const importCustomTextLabels = async ({
        projectKind,
        projectName,
        multiLingual,
        containerName,
        labelsFileName,
        defaultLanguage,
        projectDescription
    }: HandlerParams<{
        projectName: string;
        containerName: string;
        multiLingual: boolean;
        labelsFileName: string;
        defaultLanguage: string;
        projectDescription: string;
        projectKind: CustomTextProjectKind;
    }>) => {
        // Read labels file.
        const { session } = rootStore.azureStore;
        const { storageToken } = session.userTokens;
        const { ownedStorageAccountInfo } = rootStore.azureStore.session.csAccountStore;

        type ProjectAssetsType = CustomTextExportedProjectSwagger['assets'] | CustomTextExportedProjectSwaggerOldVersion['assets'];

        let assets: ProjectAssetsType = null;
        const fileContent = await azureBlobService.getAzureBlobContent<
            { assets: ProjectAssetsType; metadata: CustomTextExportedProjectSwaggerOldVersion['metadata'] } | ProjectAssetsType
        >({
            storageToken,
            containerName,
            blobName: labelsFileName,
            storageAccountName: ownedStorageAccountInfo.name
        });

        assets = (<CustomTextExportedProjectSwaggerOldVersion>fileContent).assets ?? (fileContent as ProjectAssetsType);

        let oldVersionProject = null;

        const getOldVersionModelType = (): CustomTextModelTypeSwaggerOldVersion => {
            switch (projectKind) {
                case 'CustomMultiLabelClassification':
                    return 'multiClassification';
                case 'CustomSingleLabelClassification':
                    return 'singleClassification';
                default:
                    return 'extraction';
            }
        };

        if (
            (assets as CustomTextExportedProjectSwaggerOldVersion['assets']).classifiers ||
            (assets as CustomTextExportedProjectSwaggerOldVersion['assets']).extractors
        ) {
            oldVersionProject = {
                assets,
                metadata: {
                    multiLingual,
                    settings: {},
                    name: projectName,
                    language: defaultLanguage,
                    description: projectDescription,
                    modelType: getOldVersionModelType(),
                    storageInputContainerName: containerName
                },
                stringIndexType: 'Utf16CodeUnit'
            };
        }

        // Import custom text application.
        await getCustomTextProjectService().initiateAndPollImportProject({
            projectName,
            projectToImport:
                oldVersionProject ||
                ({
                    assets,
                    metadata: {
                        projectKind,
                        multiLingual,
                        settings: {},
                        projectName,
                        language: defaultLanguage,
                        description: projectDescription,
                        storageInputContainerName: containerName
                    },
                    stringIndexType: 'Utf16CodeUnit'
                } as CustomTextExportedProjectSwagger),
            operationHandler: getImportPollOperationHandler(),
            apiVersion:
                (<CustomTextExportedProjectSwaggerOldVersion>fileContent).projectFileVersion ??
                (oldVersionProject ? LuisConstants.CustomText.ImportProjectOldVersion : LuisConstants.CustomText.AuthoringApiVersion)
        });
    };

    const exportCustomTextProject = async ({
        projectName,
        trainedModelName
    }: HandlerParams<{ projectName: string; trainedModelName?: string }>) => {
        await getCustomTextProjectService().exportProject({ projectName, trainedModelName });
    };

    const importCustomTextProject = async ({
        file,
        newProjectName,
        isExtractionProject,
        tracker
    }: HandlerParams<{ file: File; isExtractionProject: boolean; newProjectName?: string }>) => {
        const fileStringifiedContent = await fileService.readFromDisk(file);
        let fileJsonContent: CustomTextExportedProjectSwagger;

        const invalidJsonError = new Error();
        invalidJsonError.name = 'Invalid format';
        invalidJsonError.message = 'The file provided is of invalid JSON format.';

        try {
            fileJsonContent = JSON.parse(fileStringifiedContent);
        } catch {
            throw invalidJsonError;
        }

        if (
            !fileJsonContent.assets ||
            !fileJsonContent?.metadata?.language ||
            !fileJsonContent?.metadata?.projectName ||
            !fileJsonContent?.metadata?.storageInputContainerName ||
            !fileJsonContent?.metadata?.projectKind ||
            (isExtractionProject && fileJsonContent?.metadata?.projectKind !== 'CustomEntityRecognition')
        ) {
            throw invalidJsonError;
        }

        const projectName = newProjectName || fileJsonContent.metadata.projectName;

        await getCustomTextProjectService().initiateAndPollImportProject({
            projectName,
            projectToImport: { ...fileJsonContent, metadata: { ...fileJsonContent.metadata, projectName } },
            operationHandler: getImportPollOperationHandler(),
            apiVersion: fileJsonContent.projectFileVersion ?? LuisConstants.CustomText.AuthoringApiVersion
        });

        if (tracker) {
            tracker.setContext(projectName);
        }
    };

    const resetCustomTextCultureStoreHouseTracker = async () => {
        rootStore.customTextCultureStorehouse.tracker.reset();
    };

    return {
        getCustomTextCultures,
        getCustomTextProjects,
        copyCustomTextProject,
        closeCustomTextProject,
        deleteCustomTextProject,
        createCustomTextProject,
        updateCustomTextProject,
        importCustomTextLabels,
        exportCustomTextProject,
        importCustomTextProject,
        resetStoreToExportedData,
        getExtractionExportProject,
        updateCustomTextProjectAssets,
        getCustomTextNextBatchOfDocuments,
        getCustomTextSpecificBatchWithPrefix,
        openCustomTextProjectWithBatchLoading,
        rebindStorageAccountToCurrentCsAccount,
        resetCustomTextCultureStoreHouseTracker,
        fetchCustomTextProjectAvailableLabelsFileNames
    };
};
